"use client"

import React, {useEffect, useRef, useState} from "react";

import Image from "next/image";
import DecorationLine from "@/components/global/ui/DecorationLine";
import DecorationSquares from "@/components/global/ui/DecorationSquares";
import Chip from "@/components/global/ui/Chip";

import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import {CTACornerButton} from "@/shadcn/ui/corner-button";
import gsap from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger";

import {PageAboutSchema} from "@/sanity/pages/PageAboutTemplate";

import quotes from "@/public/about/quotes.svg";

gsap.registerPlugin(ScrollTrigger);

export default function PeopleSection({
  data,
  className
}: {
  data: PageAboutSchema;
  className?: string;
}) {
  const chipRefs = useRef<(HTMLDivElement | null)[]>([]);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [chipPositions, setChipPositions] = useState<number[]>([]);
  const [maxCardHeight, setMaxCardHeight] = useState<number | null>(null);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [activeCardIndex, setActiveCardIndex] = useState(0);

  useEffect(() => {
    const calculatePositions = () => {
      if (!chipRefs.current.length) return;

      const allChipsMeasured = chipRefs.current.every(chip => chip && chip.offsetWidth > 0);
      if (!allChipsMeasured) return;

      const positions: number[] = [];
      let cumulativeLeft = 0;

      const remInPixels = parseFloat(getComputedStyle(document.documentElement).fontSize);

      chipRefs.current.forEach((chip) => {
        positions.push(cumulativeLeft);
        if (chip) {
          const widthInPixels = chip.offsetWidth;
          const widthInRem = widthInPixels / remInPixels;
          cumulativeLeft += widthInRem + 1;
        }
      });

      setChipPositions(positions);
    };

    const timeout1 = setTimeout(calculatePositions, 100);
    const timeout2 = setTimeout(calculatePositions, 300);
    const timeout3 = setTimeout(calculatePositions, 500);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(calculatePositions);
    }

    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculatePositions, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, [data?.peopleItems]);

  useEffect(() => {
    const calculateMaxHeight = () => {
      if (!cardRefs.current.length || maxCardHeight !== null) return;

      let maxHeightInPixels = 0;

      cardRefs.current.forEach((card) => {
        if (card) {
          const height = card.offsetHeight;
          if (height > maxHeightInPixels) {
            maxHeightInPixels = height;
          }
        }
      });

      if (maxHeightInPixels > 0) {
        const remInPixels = parseFloat(getComputedStyle(document.documentElement).fontSize);
        const maxHeightInRem = maxHeightInPixels / remInPixels;
        setMaxCardHeight(maxHeightInRem);
      }
    };

    const timeout1 = setTimeout(calculateMaxHeight, 150);
    const timeout2 = setTimeout(calculateMaxHeight, 400);
    const timeout3 = setTimeout(calculateMaxHeight, 800);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        setTimeout(calculateMaxHeight, 100);
      });
    }

    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculateMaxHeight, 200);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, [data?.peopleItems, maxCardHeight]);

  useEffect(() => {
    const handleResize = () => {
      const isLg = window.innerWidth >= 993;
      setIsLargeScreen(isLg);
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!cardRefs.current.length || !isLargeScreen) return;

    const triggers = cardRefs.current.map((card, index) => {
      if (!card) return null;

      return ScrollTrigger.create({
        trigger: card,
        start: "top 50%",
        end: "bottom 50%",
        onEnter: () => setActiveCardIndex(index),
        onEnterBack: () => setActiveCardIndex(index),
      });
    });

    return () => {
      triggers.forEach(trigger => trigger?.kill());
    };
  }, [data?.peopleItems, isLargeScreen]);
  return (
    <section className={cn(
      "relative py-[12rem] lg:py-[22.5rem]",
      className
    )}>
      <DecorationLine
        className="left-0 top-0"
        orientation="horizontal"
      />
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="left"
      />
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="right"
      />
      <DecorationSquares
        className="top-[2px]"
      />
      <div className="container relative flex flex-col items-center">
        <div className="w-[min(191.75rem,100%)] flex flex-col lg:flex-row items-center lg:items-start lg:justify-between gap-[7rem] lg:gap-0">
          <div
            className="w-[min(61.25rem,100%)] flex flex-col items-center lg:items-start lg:sticky"
            style={{
              ...(maxCardHeight !== null && isLargeScreen && {
                top: `calc(50vh - ${(maxCardHeight / 2) + 5}rem)`
              })
            }}
          >
            <Chip
              text={data?.peopleChip?.text}
              image={data?.peopleChip?.icon}
            />

            {data?.peopleText && (
              <div className="richtext lg:richtext-start prose-headings:w-[min(38rem,100%)] sm:prose-headings:w-[min(46rem,100%)] lg:prose-headings:w-[min(64.375rem,100%)]">
                <PortableText
                  value={data.peopleText}
                  components={richTextComponent}
                />
              </div>
            )}

            {data?.peopleCTA && (
              <CTACornerButton
                className="mt-[2rem]"
                data={data.peopleCTA}
              />
            )}

            {data?.peopleItems && (
              <div className="mt-[18.25rem] relative hidden lg:block">
                {data.peopleItems.map((item, index) => (
                  <div
                    key={index}
                    className={cn(
                      "transition-opacity duration-500",
                      index === 0 ? "relative" : "absolute inset-0",
                      activeCardIndex === index ? "opacity-100" : "opacity-0"
                    )}
                  >
                    {item?.image && (
                      <Image
                        className="!w-100 hidden lg:block [clip-path:polygon(0_0,calc(100%-3.5rem)_0,100%_3.5rem,100%_100%,0_100%)] aspect-[490/361] object-cover"
                        src={item.image}
                        alt={`photo of ${item?.name}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {data?.peopleItems && (
            <div className="w-[min(111rem,100%)] flex flex-col gap-[7rem] lg:gap-[16.25rem]">
              {data?.peopleItems.map((item, index) => (
                <div
                  key={index}
                  className="lg:sticky"
                  style={{
                    ...(maxCardHeight !== null && isLargeScreen && {
                      top: `calc(50vh - ${maxCardHeight / 2}rem)`
                    })
                  }}
                >
                  {item?.image && (
                    <Image
                      className="!w-[min(92rem,100%)] lg:hidden"
                      src={item.image}
                      alt={`photo of ${item?.name}`}
                    />
                  )}

                  <div
                    ref={el => {
                      (cardRefs.current[index] = el);
                    }}
                    className="p-[3rem] sm:p-[5rem] md:p-[7rem] lg:p-[11rem] relative bg-white flex flex-col justify-between"
                    style={{
                      borderTop: `4px solid ${item?.color}`,
                      ...(maxCardHeight !== null && isLargeScreen && {
                        height: `${maxCardHeight}rem`
                      })
                    }}
                  >
                    <div
                      ref={el => {
                        (chipRefs.current[index] = el);
                      }}
                      className={cn(
                        "inline-flex items-center gap-[1rem] min-h-[5rem] px-[2rem] prose-p:text-black",
                        "[clip-path:polygon(0_0,calc(100%-1.875rem)_0,100%_1.875rem,100%_100%,0_100%)]",
                        "absolute bottom-full -left-[1px]",
                      )}
                      style={{
                        backgroundColor: `${item?.color}`,
                        ...(chipPositions[index] !== undefined && isLargeScreen && {
                          left: `${chipPositions[index]}rem`
                        })
                      }}
                    >
                      <div className="w-[3rem] h-[3rem] border border-black/20 rounded-full font-geistmono flex items-center justify-center text-black/60">{index + 1}</div>
                      <p className="font-geistmono mb-0 leading-none text-[2rem]">{item?.nickname}</p>
                    </div>

                    <div className="flex flex-col mb-[3rem] lg:mb-[8rem]">
                      <h3>{item?.name}</h3>
                      <p className="mb-0 leading-none text-[2.25rem] lg:text-[2.5rem] text-black/70">{item?.position}</p>

                      <hr className="border-t border-t-black/10 w-full my-[2rem] lg:my-[3rem]" />

                      <div className="richtext richtext-start prose-strong:text-black prose-strong:font-normal prose-p:mb-[1em] prose-p:text-[2rem]">
                        <PortableText
                          value={item.text}
                          components={richTextComponent}
                        />
                      </div>
                    </div>

                    <div className="w-full flex">
                      <div
                        className="w-[4.125rem] flex-shrink-0 flex justify-center items-center"
                        style={{
                          backgroundColor: `${item?.color}`
                        }}
                      >
                        <Image
                          className="!w-[2.125rem]"
                          src={quotes}
                          alt="quotes"
                        />
                      </div>

                      <div
                        className="py-[2rem] pl-[1.5rem] pr-[2.5rem] lg:pr-[4rem] grow"
                        style={{
                          backgroundColor: `color-mix(in srgb, ${item?.color} 50%, transparent)`
                        }}
                      >
                        <p className="mb-[.5rem] font-geistmono text-black font-medium text-[1.75rem] uppercase">{item?.quote?.title}</p>
                        <div className="richtext richtext-start prose-p:text-[2rem] prose-p:leading-[1.3]">
                          <PortableText
                            value={item?.quote?.text}
                            components={richTextComponent}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
